/**
 * Data & Analytics vertical — the second Extern Labs sprint, authored directly against the
 * {@link VerticalPack} contract (finance is an ADAPTER over a shipped `_data` layer; this pack has no
 * prior life, so it is written natively and is its own source of truth).
 *
 * THE AXIS: the two worlds a data career splits into.
 *   pole a — Analytics / decision support: partner with the business, explain what happened and why,
 *            make someone act.
 *   pole b — Data Science / engineering: build the models and the pipelines, predict what happens next,
 *            automate the answer.
 *
 * THE ARTIFACT. Finance's artifact is an income statement; this vertical's is a PRODUCT FUNNEL. That
 * substitution is the whole design: a funnel is the first thing any data person is handed, it is
 * readable without domain training, and — unlike an income statement — its rows are survivors of a
 * step rather than terms in an equation. (See `data-analytics-copy.ts` for why the primer's operators
 * are `↓` rather than `−` / `=`.)
 *
 * ⚠️ M4 NUMBERS. Every figure in `M4_SUBJECTS` is a real, publicly reported metric (Q4 of each fiscal
 * year, from the company's own shareholder letter / press release). Nothing is interpolated, smoothed
 * or invented. Two consequences the reader should know about:
 *
 *   1. THE SUBJECT LIST IS NOT THE ONE THAT WAS BRIEFED. Netflix, Pinterest and Roblox were dropped:
 *      each publishes exactly ONE user metric (Netflix: paid memberships — and it stopped reporting
 *      even that after Q4 2024; Pinterest: global MAU; Roblox: DAU, and it explicitly declines to
 *      report MAU because of duplicate accounts). A subject with one row cannot support the
 *      `engagement` chip group, which needs a ratio of two rows, so grading them would have meant
 *      fabricating a row. Reddit and Snap replace them: both publish a genuine two-tier funnel.
 *   2. ROWS ARE PER-SUBJECT. No two consumer companies report the same funnel, so each subject carries
 *      the rows IT actually discloses. The skeleton is constant even where the labels are not:
 *      row 1 = the audience, row 2 = the tier that went deeper, then the paying tier and its net adds
 *      where a paying tier exists. `engagement` is always graded on row-2-over-row-1 — the share of
 *      the audience that goes deeper — which is the one read every one of these tables supports.
 */

import { LineChart } from 'lucide-react';

import { DATA_DISCOVERY_EVENTS } from '@/lib/analytics/data-discovery-events';

import type {
  LabsAnswerKey,
  LabsArchetype,
  LabsAxis,
  LabsChipGroup,
  LabsDerivedMetric,
  LabsMindsetQuestion,
  LabsRecommendation,
  LabsSubject,
  LabsSwipeCard,
  VerticalPack,
} from '../_engine/types';
import { DATA_ANALYTICS_COPY } from './data-analytics-copy';

// ---------------------------------------------------------------------------
// The axis
// ---------------------------------------------------------------------------

/**
 * Teal for the decision side, indigo for the systems side, and the same gold finance uses for the
 * learner who is fluent in both — the "both poles" accent is deliberately shared across verticals so a
 * POLYMATH reveal reads as the same outcome wherever a learner meets it.
 */
const DATA_AXIS: LabsAxis = {
  bothAccent: '#B8860B',
  poles: {
    // The axis is a TEMPERAMENT split, not a job-title split: pole A's output is a human decision, pole
    // B's output is a system that runs without you. Labelled by job family it would be wrong twice over —
    // "Data Science" names work we do not teach, and it wrongly excludes the SQL/schema/statistics work
    // we do.
    a: { accent: '#0F5C63', label: 'Analytics' },
    b: { accent: '#4338CA', label: 'Engineering' },
  },
};

// ---------------------------------------------------------------------------
// Archetypes
// ---------------------------------------------------------------------------

/**
 * THE BUILDER was originally THE MODELER, and the rename is not cosmetic — it was a mis-sale.
 *
 * "Modeler" promises predictive/ML work, and an audit of the live catalog found we teach none: across
 * every active program there is no pipeline, orchestration or ML-training content, and the only
 * `sklearn` in the whole curriculum repo fits a logistic regression to a superheroes toy dataset inside
 * a healthcare course. A learner told "the data science and data engineering tracks are your home" was
 * being handed, in practice, an SQL course and two no-code visualization courses.
 *
 * What we DO teach on the technical side is real and worth wanting: SQL, star-schema design, statistical
 * rigour, scripted analysis. That is a builder, not a modeller. The axis now describes the temperament
 * split honestly — do you want to change a decision, or ship the system the answers come out of — and
 * every archetype resolves to programs that actually exist.
 */
const DATA_ARCHETYPES: Readonly<Record<string, LabsArchetype>> = {
  builder: {
    accent: DATA_AXIS.poles.b.accent,
    body: "You're drawn to structure: the query that becomes the source of truth, the schema that holds up, the result you can prove isn't noise. The SQL, database-design and statistical-analysis tracks are your home.",
    displayName: 'THE BUILDER',
    id: 'builder',
    tagline: "You'd rather ship the thing than pitch the thing.",
  },
  gen: {
    accent: DATA_AXIS.bothAccent,
    body: 'You can build the thing and then explain it to the person who has to act on it. That pairing has a name — analytics engineer — and it is one of the highest-leverage seats in data, precisely because so few people can do both halves.',
    displayName: 'THE POLYMATH',
    id: 'gen',
    tagline: 'Fluent in both halves of the stack.',
  },
  translator: {
    accent: DATA_AXIS.poles.a.accent,
    body: "You're drawn to the question behind the data: who has to act, and what would actually change their mind. The analytics, visualization and data storytelling tracks are your home.",
    displayName: 'THE TRANSLATOR',
    id: 'translator',
    tagline: 'Turns messy data into decisions people act on.',
  },
};

// ---------------------------------------------------------------------------
// M2 scoring
// ---------------------------------------------------------------------------

/** The seven slider stops are 0–6. Stop 3 is the exact centre: it leans to NEITHER pole. */
const SLIDER_CENTRE = 3;

/**
 * Score the four M2 sliders into an archetype by TALLYING BOTH POLES against each other.
 *
 * Finance scores this differently, and its way is wrong — deliberately not copied. Finance counts only
 * the answers that land on ONE pole, so everything else (including the explicit centre) is implicitly
 * scored for the other pole. The consequence, verified in the browser: a learner who answers
 * "no strong lean" on all four questions gets the SAME archetype as a learner who answered hard
 * pole-B on all four. The slider literally announces `aria-valuetext="Split — no strong lean"` and the
 * sprint then tells that person they are a systems-builder. The instrument contradicts itself.
 *
 * It is not an exotic edge case either: every slider STARTS at the centre, so a learner who under-answers
 * drifts to pole B by default rather than to the generalist.
 *
 * Here, a centre answer counts for nobody, and the poles are compared to each other:
 *   more Analytics answers  → THE TRANSLATOR
 *   more Engineering answers → THE BUILDER
 *   a tie (including all-centre, and including a never-touched quiz) → THE POLYMATH
 *
 * The tie case subsumes the empty-answers guard: no answers means 0 vs 0, which is a tie, which is the
 * generalist — which is exactly what the reveal and exit screens expect as the neutral default.
 *
 * ⚠️ FINANCE HAS THE SAME DEFECT (all-centre resolves to THE ANALYST). It is NOT fixed there: finance is
 * live and mid-experiment, and changing how it scores would re-label learners mid-flight and break the
 * comparability of the causal read. Fix it when finance migrates onto the engine.
 */
function deriveArchetype(answers: Readonly<Record<string, number>>): string {
  let analytics = 0;
  let engineering = 0;

  for (const value of Object.values(answers)) {
    if (value < SLIDER_CENTRE) {
      analytics += 1;
    } else if (value > SLIDER_CENTRE) {
      engineering += 1;
    }
  }

  if (analytics === engineering) {
    return 'gen';
  }
  return analytics > engineering ? 'translator' : 'builder';
}

// ---------------------------------------------------------------------------
// M1 — the swipe deck
// ---------------------------------------------------------------------------

/**
 * Eight activities, alternating poles (a, b, a, b, …) so the deck never lets a learner straight-swipe
 * one side. Each one is a concrete thing a data person does on an ordinary Tuesday — the question the
 * card asks is "would I want to do this every day?", which only works if the activity is specific
 * enough to picture. The pole is hidden from the learner; it only drives the advisory M1 lean.
 */
const M1_CARDS: readonly LabsSwipeCard[] = [
  { icon: '📊', id: 'm1-1', pole: 'a', text: 'Build the dashboard the exec team opens every Monday' },
  { icon: '🗂️', id: 'm1-2', pole: 'b', text: 'Turn six messy spreadsheets into one database the whole team can query' },
  { icon: '🤝', id: 'm1-3', pole: 'a', text: 'Sit with the sales team and work out why one region is down' },
  { icon: '🔍', id: 'm1-4', pole: 'b', text: 'Run the test that proves the pattern is real and not just noise' },
  { icon: '✍️', id: 'm1-5', pole: 'a', text: 'Write the weekly metrics narrative for the leadership team' },
  { icon: '🧱', id: 'm1-6', pole: 'b', text: 'Design how the data should be structured before anyone writes a query' },
  { icon: '🎯', id: 'm1-7', pole: 'a', text: 'Talk a product manager out of shipping with the wrong success metric' },
  { icon: '⚙️', id: 'm1-8', pole: 'b', text: 'Write the query that becomes the number everyone quotes' },
];

// ---------------------------------------------------------------------------
// M2 — the mindset sliders
// ---------------------------------------------------------------------------

/**
 * Four situations, each with two REASONABLE instincts — the failure mode of a mindset quiz is one
 * obviously-correct end, so both anchors here are things a good data person genuinely does.
 *
 * `scoredSide` is 'left' on every question because `deriveArchetype` reads the raw slider value (0–2 =
 * Analytics). A right-scored question would need the engine to invert the value, which it does not do,
 * so the Analytics anchor must always sit on the left.
 */
const M2_QUESTIONS: readonly LabsMindsetQuestion[] = [
  {
    id: 'q1',
    leftLabel: 'Go ask the growth team',
    q: "Signups are down 15% this week. What's your first move?",
    rightLabel: 'Go query the funnel',
    scoredSide: 'left',
  },
  {
    id: 'q2',
    leftLabel: 'A dashboard people use',
    q: 'You have one free week and nobody has asked you for anything. What do you build?',
    rightLabel: 'A clean, queryable dataset',
    scoredSide: 'left',
  },
  {
    id: 'q3',
    leftLabel: 'Ship it with caveats',
    q: 'The exec wants an answer by Friday, and the underlying data is a mess.',
    rightLabel: 'Fix the data first',
    scoredSide: 'left',
  },
  {
    // AI now writes a competent query in seconds, which is squarely the automatable half of this job.
    // What it cannot do is decide which question is worth asking, or get an organisation to act. Asking
    // the learner where their value sits is the live question in the field, and it splits the poles
    // honestly rather than flattering either.
    id: 'q4',
    leftLabel: 'Asking the right question',
    q: 'An AI writes the query in four seconds. Where does your value sit?',
    rightLabel: 'Building what it runs on',
    scoredSide: 'left',
  },
];

// ---------------------------------------------------------------------------
// M3 — the practice subject (PulseApp) + the three rates
// ---------------------------------------------------------------------------

/**
 * PulseApp — the fictional practice product (finance's NovaTech). Fictional ON PURPOSE: the numbers are
 * built to teach ONE lesson cleanly, which no real company's funnel does.
 *
 * The lesson: every rate BELOW the top is improving (activation 50.0% → 51.6% → 53.7%, paid conversion
 * of activated users 33% → 36%) while the signup rate quietly falls (12.0% → 11.1% → 10.0%) and traffic
 * more than doubles. Growth is being BOUGHT at the top of the funnel, not earned. The reveal copy in
 * `data-analytics-copy.ts` states those percentages literally, so these cells and that paragraph move
 * together.
 */
const M3_SUBJECT: LabsSubject = {
  accent: DATA_AXIS.poles.a.accent,
  id: 'pulseapp',
  logo: '',
  monogram: 'P',
  name: 'PulseApp',
  periods: ['FY2022', 'FY2023', 'FY2024'],
  rows: [
    { key: 'visitors', label: 'Visitors', values: [400, 560, 820] },
    { key: 'signups', label: 'Signups', values: [48, 62, 82] },
    { emphasis: 'subtotal', key: 'activated', label: 'Activated Users', values: [24, 32, 44] },
    { key: 'retained', label: 'Retained (Month 3)', values: [14, 19, 27] },
    { emphasis: 'subtotal', key: 'paying', label: 'Paying Users', values: [8, 11, 16] },
  ],
  subtitle: '',
  tagline: 'Practice example',
  tags: [],
  unit: { prefix: '', suffix: 'k' },
};

/**
 * The three practice rates. Each wrong option is a REAL mis-derivation a beginner makes, not a random
 * near-miss — dividing by the wrong base, reading the wrong year's column, or computing the next step
 * down the funnel by mistake. Options are stored canonically; the engine shuffles their positions.
 */
const M3_METRICS: readonly LabsDerivedMetric[] = [
  {
    correct: 105.0,
    formula: '(820 − 400) ÷ 400 × 100',
    key: 'visitorGrowth',
    name: 'Visitor Growth',
    // 46.4 = the FY2024-only leg (820 ÷ 560); 51.2 = dividing the change by the END year, not the base.
    options: [46.4, 51.2, 105.0],
    period: '(2 year)',
    sources: [
      { periodIndex: 0, row: 'visitors' },
      { periodIndex: 2, row: 'visitors' },
    ],
  },
  {
    correct: 10.0,
    formula: '82 ÷ 820 × 100',
    key: 'signupRate',
    name: 'Signup Rate',
    // 12.0 = FY2022's signup rate (right formula, wrong column); 14.6 = FY2024 signups over FY2023
    // visitors (the classic off-by-one-year denominator).
    options: [10.0, 12.0, 14.6],
    period: '(FY2024)',
    sources: [
      { periodIndex: 2, row: 'signups' },
      { periodIndex: 2, row: 'visitors' },
    ],
  },
  {
    correct: 53.7,
    formula: '44 ÷ 82 × 100',
    key: 'activationRate',
    name: 'Activation Rate',
    // 5.4 = activated over VISITORS (skipping a funnel step); 61.4 = retained over activated (the
    // NEXT rate down the funnel).
    options: [5.4, 53.7, 61.4],
    period: '(FY2024)',
    sources: [
      { periodIndex: 2, row: 'activated' },
      { periodIndex: 2, row: 'signups' },
    ],
  },
];

/** ±tolerance on a picked rate, so a rounding difference still counts as right (finance uses the same). */
const METRIC_TOLERANCE = 1.0;

/** Every option on the M3 rate picker is a percentage. */
const METRIC_UNIT_SUFFIX = '%';

// ---------------------------------------------------------------------------
// M4 — the real subjects
// ---------------------------------------------------------------------------

/** Every M4 subject reports its users in millions, and every figure is the Q4 average for that year. */
const M4_UNIT = { prefix: '', suffix: 'M' } as const;

/** The three reported years, shared by all four subjects. */
const M4_PERIODS: readonly string[] = ['FY2022', 'FY2023', 'FY2024'];

/**
 * The four real products. Each table is the company's OWN reported metrics — Q4 averages from its
 * shareholder letter — and each supports the same three reads:
 *
 *   growth      → the trend in row 1 (the audience)
 *   engagement  → the trend in row 2 ÷ row 1 (the share of the audience that goes deeper)
 *   risk/opp    → interpretive, never graded
 *
 * Duolingo is the only one of the four that reports a textbook DAU/MAU stickiness pair. For the others,
 * "goes deeper" is the tier that company actually discloses: Spotify's Premium subscribers inside its
 * MAU, Reddit's logged-in users inside its daily uniques, Snap's Snapchat+ subscribers inside its DAU.
 * Every one of those is a genuine engagement-depth ratio; none of them is invented to fill a row.
 */
const M4_SUBJECTS: Readonly<Record<string, LabsSubject>> = {
  duolingo: {
    accent: '#58CC02',
    id: 'duolingo',
    logo: '/images/labs/data/duolingo.svg',
    monogram: 'D',
    name: 'Duolingo',
    periods: M4_PERIODS,
    // DAU/MAU: 26.9% → 30.4% → 34.7% — the cleanest stickiness improvement in consumer software, and
    // Duolingo quotes the ratio itself. Only ~8% of MAU pay, which is where the read gets interesting.
    rows: [
      { key: 'mau', label: 'Monthly Active Users', values: [60.7, 88.4, 116.7] },
      { key: 'dau', label: 'Daily Active Users', values: [16.3, 26.9, 40.5] },
      { emphasis: 'subtotal', key: 'paying', label: 'Paid Subscribers', values: [4.2, 6.6, 9.5] },
      { emphasis: 'subtotal', key: 'netAdds', label: 'Net New Paid Subscribers', values: [1.3, 2.4, 2.9] },
    ],
    subtitle: 'DUOL',
    tagline: 'Language learning · free-to-paid · gamified habit',
    tags: ['Edtech', 'Freemium', 'Habit Loop'],
    unit: M4_UNIT,
  },
  reddit: {
    accent: '#FF4500',
    id: 'reddit',
    logo: '/images/labs/data/reddit.svg',
    monogram: 'R',
    name: 'Reddit',
    periods: M4_PERIODS,
    // The two halves SUM to the daily-uniques row exactly (27.6 + 29.9 = 57.5, and so on) — the only
    // subject whose table closes arithmetically, which makes the logged-in share easy to see. There is
    // no paying row because there is no paying tier: Reddit is ad-funded, and that absence IS the read.
    rows: [
      { key: 'dau', label: 'Daily Active Uniques', values: [57.5, 73.1, 101.7] },
      { emphasis: 'subtotal', key: 'loggedIn', label: 'Logged-In Daily Uniques', values: [27.6, 36.4, 46.1] },
      { key: 'loggedOut', label: 'Logged-Out Daily Uniques', values: [29.9, 36.7, 55.6] },
    ],
    subtitle: 'RDDT',
    tagline: 'Communities · ad-funded · search-driven growth',
    tags: ['Social', 'Ad-Funded', 'No Paid Tier'],
    unit: M4_UNIT,
  },
  snap: {
    // NOT Snapchat's brand yellow (#FFFC00): every other subject's accent is a tile background with
    // white text on it, and white on that yellow is unreadable. This is the darkest gold that still
    // reads as Snap and still passes contrast.
    accent: '#A67C00',
    id: 'snap',
    logo: '/images/labs/data/snapchat.svg',
    monogram: 'S',
    name: 'Snap',
    periods: M4_PERIODS,
    // Snapchat+ launched mid-2022, so FY2022's net adds ARE its whole base. Subscription is still ~3%
    // of DAU — tiny next to ads, but the fastest-growing line in the table.
    rows: [
      { key: 'dau', label: 'Daily Active Users', values: [375, 414, 453] },
      { emphasis: 'subtotal', key: 'paying', label: 'Snapchat+ Subscribers', values: [2.0, 7.0, 14.0] },
      { emphasis: 'subtotal', key: 'netAdds', label: 'Net New Snapchat+ Subscribers', values: [2.0, 5.0, 7.0] },
    ],
    subtitle: 'SNAP',
    tagline: 'Messaging · ad-funded · new subscription tier',
    tags: ['Social', 'Ad-Funded', 'Young Subscription'],
    unit: M4_UNIT,
  },
  spotify: {
    accent: '#1DB954',
    id: 'spotify',
    logo: '/images/labs/data/spotify.svg',
    monogram: 'S',
    name: 'Spotify',
    periods: M4_PERIODS,
    // Premium ÷ MAU: 41.9% → 39.2% → 39.0%. The audience grows faster than the paying core, so the paid
    // mix DILUTES even while Premium sets records — the subtlest read of the four.
    rows: [
      { key: 'mau', label: 'Monthly Active Users', values: [489, 602, 675] },
      { key: 'adMau', label: 'Ad-Supported MAU', values: [295, 379, 425] },
      { emphasis: 'subtotal', key: 'paying', label: 'Premium Subscribers', values: [205, 236, 263] },
      { emphasis: 'subtotal', key: 'netAdds', label: 'Net New Premium Subscribers', values: [25, 31, 27] },
    ],
    subtitle: 'SPOT',
    tagline: 'Audio streaming · free tier funnels into Premium',
    tags: ['Streaming', 'Freemium', 'Two-Sided Mix'],
    unit: M4_UNIT,
  },
};

const M4_GROUPS: readonly LabsChipGroup[] = [
  {
    chips: [
      { id: 'accel', label: 'Accelerating growth' },
      { id: 'decel', label: 'Decelerating growth' },
      { id: 'steady', label: 'Steady / stable' },
      { id: 'volatile', label: 'Volatile / mixed signals' },
    ],
    graded: true,
    id: 'growth',
    label: 'Growth story',
  },
  {
    // "Engagement depth", not "stickiness". Only Duolingo reports a textbook DAU/MAU stickiness pair; for
    // the other three the graded ratio is the share of the audience that goes DEEPER (Spotify's Premium
    // inside its MAU, Reddit's logged-in inside its daily uniques, Snap's Snapchat+ inside its DAU). Each
    // is a real engagement-depth measure, but none is stickiness, and a data person would notice — which
    // matters for a sprint whose whole pitch is that it is honest about the work.
    chips: [
      { id: 'improving', label: 'Depth improving' },
      { id: 'declining', label: 'Depth declining' },
      { id: 'stable', label: 'Depth stable' },
      { id: 'mixed', label: 'Mixed across years' },
    ],
    graded: true,
    id: 'engagement',
    label: 'Engagement depth',
  },
  {
    // Interpretive: a defensible case exists for every chip here, so the engine never grades it red.
    chips: [
      { id: 'monetization', label: 'Monetization upside' },
      { id: 'decay', label: 'Engagement decay' },
      { id: 'saturation', label: 'Market saturation' },
      { id: 'conversion', label: 'Conversion upside' },
    ],
    graded: false,
    id: 'riskOpp',
    label: 'Biggest risk or opportunity',
  },
];

/**
 * The intended read of each subject — every answer is derivable from the table above, and nothing else.
 *
 *   duolingo → MAU +45.6% then +32.0% (decelerating in RATE, still enormous); DAU/MAU 26.9 → 30.4 →
 *              34.7% (improving); only 8.1% of MAU pay, on the stickiest base in the set → conversion.
 *   reddit   → daily uniques +27.1% then +39.1% (accelerating); logged-in share 48.0 → 49.8 → 45.3%
 *              (up, then down — mixed); 101.7M daily uniques and no paid tier at all → monetization.
 *   snap     → DAU +10.4% then +9.4% (steady); Snapchat+ 0.5 → 1.7 → 3.1% of DAU (improving); at 3%
 *              penetration the subscription line is still a rounding error next to ads → monetization.
 *   spotify  → MAU +23.1% then +12.1% (decelerating); Premium share of MAU 41.9 → 39.2 → 39.0%
 *              (declining); growth is landing in the free tier of maturing markets → saturation.
 */
const M4_ANSWER_KEY: Readonly<Record<string, LabsAnswerKey>> = {
  duolingo: { engagement: 'improving', growth: 'decel', riskOpp: 'conversion' },
  reddit: { engagement: 'mixed', growth: 'accel', riskOpp: 'monetization' },
  snap: { engagement: 'improving', growth: 'steady', riskOpp: 'monetization' },
  spotify: { engagement: 'declining', growth: 'decel', riskOpp: 'saturation' },
};

/** Curated 3-bullet reads — editorial, not a live model. Same length and register as finance's. */
const M4_SAMPLE_ANALYSES: Readonly<Record<string, string>> = {
  duolingo:
    '• Growth is decelerating in rate and accelerating in dollars-worth-of-users. MAU went 60.7M → 88.4M → 116.7M, which is +46% then +32%: each leg is slower in percentage terms, but the ABSOLUTE adds keep climbing (27.7M then 28.3M). At this size that is the healthier signal. Do not read "decelerating" as "slowing down" — read it as "the base got bigger".\n\n' +
    '• The real story is stickiness. DAU/MAU went 26.9% → 30.4% → 34.7%, which is extraordinary for a consumer app — most sit in the teens and drift down as they scale. Duolingo added 56M monthly users over two years and STILL got a bigger share of them to show up daily. That is the gamification and the streak mechanic doing exactly what they were designed to do, and it is the moat.\n\n' +
    '• Monetization is the gap. Paid subscribers went 4.2M → 6.6M → 9.5M, but that is still only 8.1% of MAU — so 107 million people use this product every month without paying for it. The bull case is that a base this engaged is the easiest conversion problem in software. The bear case is that free is good enough, and the streak that drives engagement is exactly the thing users would not pay to protect.',

  reddit:
    '• Growth is genuinely accelerating: daily uniques went 57.5M → 73.1M → 101.7M, or +27% then +39%. An accelerating second leg on a 20-year-old product is rare enough to be suspicious, and the table tells you why to be suspicious — look at where the growth came from.\n\n' +
    '• Split the base. Logged-in daily uniques grew 27.6M → 36.4M → 46.1M (+67% over two years). Logged-OUT grew 29.9M → 36.7M → 55.6M (+86%). So the logged-in share of the audience went 48.0% → 49.8% → 45.3%: it improved, then fell. Most of the recent growth is drive-by traffic arriving from a search engine, not people who came back on purpose. That traffic is real, it is monetizable, and it is rented — an algorithm change at Google takes it away without asking.\n\n' +
    '• There is no paying row in this table, and that is the point: Reddit is entirely ad-funded, so 101.7M daily uniques convert into exactly zero subscription revenue. Risk: the fastest-growing half of the audience is the half that is least attached and least valuable per user. Opportunity: the logged-in core is now 46M people with a real identity and a real interest graph, which is a materially better ad product than the logged-out half will ever be.',

  snap:
    '• Growth is steady, not exciting: DAU went 375M → 414M → 453M, adding almost exactly 39M in each of the two years (+10% then +9%). A perfectly linear add on a decelerating percentage is what a maturing network looks like. The honest read is a business growing users reliably and slowly, in a category where the incumbent above it grows faster.\n\n' +
    '• The subscription line is the one thing moving fast. Snapchat+ went 2M → 7M → 14M — doubling in the most recent year — and its penetration of DAU went 0.5% → 1.7% → 3.1%. That is real engagement depth: 14 million people now pay for a product they used to get free. It is also, at 3% of the base, still small enough that it changes the narrative long before it changes the P&L.\n\n' +
    '• Risk: the DAU growth is concentrated in markets that monetize at a fraction of North America, so users and revenue have decoupled — this table flatters the business more than the income statement would. Opportunity: 439 million daily users who do not pay Snap a cent directly, on a subscription product that has doubled two years running.',

  spotify:
    '• Growth is decelerating: MAU went 489M → 602M → 675M, which is +23% then +12%. Premium subscribers grew more slowly still (205M → 236M → 263M, +15% then +11%). Both lines are still setting records — 675M is the largest audience in audio — but the second leg is roughly half the pace of the first, and that is the shape of a business approaching the edges of its addressable market.\n\n' +
    '• The mix is quietly moving the wrong way. Premium as a share of MAU went 41.9% → 39.2% → 39.0%. The paying core grew; the free tier grew FASTER (ad-supported MAU 295M → 379M → 425M). So Spotify is adding users who are, on average, worth less than the ones it already had — the same disease PulseApp had, one layer up the funnel.\n\n' +
    "• Risk: the incremental user increasingly lives in a market that will not pay $11 a month, so audience growth stops translating into subscription growth. Opportunity: 425 million ad-supported listeners is the largest free-to-paid conversion pool in consumer subscription, and it is Spotify's to convert — the audiobook and ads mix is the mechanism, and no competitor has a comparable on-ramp.",
};

/** Sample push-backs, auto-filled on reveal. `_default` backs any subject with no entry. */
const M4_TAKE_SAMPLES: Readonly<Record<string, string>> = {
  _default:
    "I agree with the core read, but I'd push back on how lightly the analysis treats the weakest step in this funnel. That's where the real debate is.",
  duolingo:
    "I agree that stickiness is the headline, but I'd push back on the conversion optimism. A 34.7% DAU/MAU on a free product may mean the free tier is already good enough, which makes paid HARDER to sell, not easier.",
  reddit:
    "I agree the logged-out surge is fragile, but I'd push back on treating it as low quality. Search traffic is how every logged-in user starts, so the real question is the conversion rate from one to the other, and this table doesn't show it.",
  snap: "I agree Snapchat+ is the most interesting line here, but I'd flag that doubling from a small base is easy. The number that matters is whether the second 14 million cost the same to acquire as the first.",
  spotify:
    "I agree the paid mix is diluting, but I'd push back on calling that a problem. A free listener costs Spotify almost nothing and is the only pipeline it has into paid, so a falling Premium share may just be the top of the funnel doing its job.",
};

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

/**
 * MEMBERSHIP — the catalog `tags.categories` values that put a program in this vertical at all. Also the
 * gate on this vertical's entry, so the taxonomy can never drift between the two.
 *
 * ⚠️ THIS LIST IS ALSO WHAT MAKES THE CATALOG FILTER AN INTEREST SIGNAL. The Career Paths filter builds
 * its options verbatim from the live catalog's `tags.categories`, and `use-labs-entry` matches the
 * learner's selected filter values against THIS list. A category string that exists in the catalog but
 * not here means a learner can filter straight into this vertical's programs and the entry never fires —
 * which is exactly what shipped first (QA-433): the dev catalog tags programs with the umbrella
 * 'Data & Analytics', prod uses only the granular names, so the audit against prod missed it and the
 * whole catalog-filter path was dead on the environment QA tests on.
 *
 * So the list carries BOTH tiers, and dead-on-prod strings are deliberate:
 *   • The UMBRELLA names ('Data & Analytics', 'Data Analytics') — env/tagging variants. At the 7/13
 *     prod audit they matched zero programs; either string is unambiguous enough that any program ops
 *     tags with it belongs in this vertical anyway, so including them is safe for the recommendation
 *     pool and makes filter-detection and enrollment-retirement work wherever a catalog carries them
 *     (dev today, prod after any re-tag) instead of dying silently.
 *   • The GRANULAR names — the prod taxonomy, which drives the recommendation pool.
 *
 * One value stays REMOVED after auditing the live catalog:
 *   • 'Marketing Analytics' — it dragged in Beats' Creative Advertising Strategy (survey design and ad
 *     creative, no data work) and Wayfair's AI-agent automation course. The cost is that it also drops
 *     Breaking Games' E-Commerce Data Analysis, which is a genuine data program mis-tagged by ops as
 *     Marketing Analytics + E-Commerce with no data category at all. Losing one good program beats
 *     recommending an ad-creative course to someone who just asked whether data is for them. ASK OPS TO
 *     RE-TAG IT and add it back.
 */
const DATA_CATEGORIES: readonly string[] = [
  'Data & Analytics',
  'Data Analytics',
  'Data Analytics & Visualization',
  'Data Science',
  'Data Engineering',
  'Data Storytelling',
  'Healthcare Analytics',
  'People Analytics',
];

/**
 * ORDER — which of the vertical's programs suit each archetype. Matched against each program's
 * categories PLUS its `skills` and `topics` (see `LabsRecommendationConfig`).
 *
 * It HAS to reach into skills/topics, because the category taxonomy cannot separate these archetypes:
 * ten of the eleven data programs carry 'Data Analytics & Visualization', so any priority list built on
 * categories alone matches nearly everything and the archetypes become interchangeable — the failure
 * looks exactly like success. The real distinction only exists one level down:
 *
 *   builder    → SQL Fundamentals · Analytics Database Design · Python · Statistical Research
 *                (Breaking Games' SQL/star-schema course, CIYJ's regression work, Amazon's Python
 *                 scraping + sentiment, TruBridge's Colab analysis, NY Racing's scoring model)
 *   translator → Visual Data Storytelling · Design · Market Research · Presentation Skills
 *                (Canva, Flourish and CIYJ's visualization courses, Beats' consumer research)
 *
 * `gen` keeps no priority: THE POLYMATH is the learner both halves suit, so the whole pool qualifies
 * equally and it sorts on soonest start.
 */
const ARCHETYPE_PRIORITY: Readonly<Record<string, readonly string[]>> = {
  // Deliberately NOT 'Data Science' / 'Data Engineering', even though they sound like the obvious fit.
  // Those two are MEMBERSHIP categories (they appear in DATA_CATEGORIES above), and a priority tag that
  // is also a membership category is unsafe by construction: membership categories are broad, so any
  // re-tagging by ops silently inflates the tier — which is precisely how the original mapping collapsed
  // (a priority list that matched 10 of 11 programs looked configured and selected nothing).
  // Skills/topics carry the real signal, and cost nothing: the flagship builder program matches here on
  // four of them ('SQL Fundamentals', 'Analytics Database Design', 'Database Design', 'SQL') without
  // either category being listed.
  builder: [
    'Analytics Database Design',
    'Database Design',
    'Python',
    'SQL',
    'SQL Fundamentals',
    'Statistical Research',
  ],
  gen: [],
  // NOT the bare 'Data Storytelling' tag: the SQL/star-schema course carries it as a skill, so including
  // it would promote the single most builder-shaped program in the catalog into the translator's tier.
  // 'Visual Data Storytelling' + 'Design' pick up every visualization course without that collision.
  translator: ['Consumer Insights', 'Design', 'Market Research', 'Presentation Skills', 'Visual Data Storytelling'],
};

/**
 * Static fallback cards, used ONLY when the live catalog call fails or returns nothing. Programs are
 * real Extern data programs; no data-vertical art exists yet, so the images are the generic
 * per-position placeholders and the logos are empty (the card falls back to the company name).
 * `duration` is the one field here not sourced from the catalog — treat it as provisional.
 */
const REC_IMAGES = {
  best: '/images/rec/best-match.png',
  cards: ['/images/rec/card-1.png', '/images/rec/card-2.png'],
} as const;

/**
 * Every card below names a REAL, ACTIVE program, with its real title and its real duration (verified
 * against the program registry). The previous draft invented two — "E-Commerce Data Analysis: Analytics
 * & Database Design" and "Data Visualization & Data-Driven Decision Making" were each a mash-up of two
 * separate externships — and put a no-code Canva course on the build-side archetype. A fallback card is
 * shown precisely when the live catalog is DOWN, i.e. when nobody can click through and discover the
 * program doesn't exist, so a fabricated card is a mis-sale with no safety net.
 */
const RECOMMENDATIONS: Readonly<Record<string, readonly LabsRecommendation[]>> = {
  // THE BUILDER — the technical half we actually teach: SQL, schema, statistical rigour.
  builder: [
    {
      blurb:
        'The build-side data program: turn six messy CSVs into a real analytics database — dimension tables, summary tables, the lot — then use it to answer questions the business cannot answer today.',
      company: 'Breaking Games',
      duration: '8 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['SQL', 'Database Design', 'Business Intelligence', 'E-Commerce Analytics'],
      // The title the LIVE catalog ships (confirmed against prod enrolments). The `extern-programs`
      // repo calls this program "Analytics & Database Design"; that name reaches no learner.
      title: 'SQL & Database Architecture',
      whatYoullLearn: [
        'Design a schema from raw, messy source data',
        'Write the SQL the whole team will reuse',
        'Model marketing attribution and cart abandonment',
        'Ship a database a team can actually run on',
      ],
    },
    {
      blurb:
        'Statistical rigour on data with real stakes: run the regressions and significance tests that decide whether a youth-justice policy finding is real or noise.',
      company: 'Center for Improving Youth Justice',
      duration: '8 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Python', 'Statistical Research', 'Evidence Based Policy'],
      title: 'Data-Driven Decision Making',
      whatYoullLearn: [
        'Run correlational and regression analysis in Python',
        'Tell a real relationship from a coincidence',
        'Work with data where the answer changes policy',
        'Defend a methodology, not just a number',
      ],
    },
    {
      blurb:
        'Scripted analysis end to end: scrape employee-voice data, run sentiment analysis over it, and find the root cause behind an operational problem.',
      company: 'Amazon',
      duration: '8 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['Python', 'Sentiment Analysis', 'Workforce Data Analysis'],
      title: 'Operational Strategy & People Analytics',
      whatYoullLearn: [
        'Collect and clean data with Python',
        'Run sentiment analysis over unstructured text',
        'Segment a real workforce',
        'Take a root-cause finding to an operations team',
      ],
    },
  ],
  // THE POLYMATH — both halves: one technical, one analytical, one communicative.
  gen: [
    {
      blurb:
        'A broad first data project: take a real healthcare dataset from raw table to a recommendation a clinician-facing team can act on.',
      company: 'TruBridge',
      duration: '8 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['Data Analysis', 'Exploratory Data Analysis', 'Healthcare Analytics'],
      title: 'Healthcare Data Analytics',
      whatYoullLearn: [
        'Work with a real healthcare dataset',
        'Run exploratory and correlational analysis',
        'Turn a finding into a recommendation',
        'Present the analysis to a non-technical team',
      ],
    },
    {
      blurb:
        'Both halves of the job at a brand you know: run the quantitative analysis, then reconcile it with what customers actually say.',
      company: 'Beats by Dre',
      duration: '12 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Data Analysis', 'Python', 'Quantitative & Qualitative Analysis'],
      title: 'Data Analytics: Qualitative & Quantitative Insights',
      whatYoullLearn: [
        'Blend survey and behavioural data',
        'Run sentiment analysis in Python',
        'Segment a real consumer base',
        'Brief a brand team on what to do next',
      ],
    },
    {
      blurb:
        'Turn six messy CSVs into a database, then turn the database into a decision — the technical and the communicative halves in one project.',
      company: 'Breaking Games',
      duration: '8 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['SQL', 'Database Design', 'Data Storytelling'],
      // The title the LIVE catalog ships (confirmed against prod enrolments). The `extern-programs`
      // repo calls this program "Analytics & Database Design"; that name reaches no learner.
      title: 'SQL & Database Architecture',
      whatYoullLearn: [
        'Design a schema from raw source data',
        'Query a real e-commerce funnel',
        'Build the dashboard the team runs on',
        'Recommend a merchandising change',
      ],
    },
  ],
  // THE TRANSLATOR — the decision-side: visualization, storytelling, the meeting.
  translator: [
    {
      blurb:
        'Pure data storytelling: take a real dataset and build the interactive visualization that makes a room of non-analysts see what you see.',
      company: 'Flourish',
      duration: '6 weeks',
      image: REC_IMAGES.best,
      logo: '',
      tags: ['Visual Data Storytelling', 'Data Visualization', 'Strategic Communication'],
      title: 'Data Visualization',
      whatYoullLearn: [
        'Choose the chart the argument needs',
        'Build an interactive data story',
        'Cut everything that is not the point',
        'Present a finding to a lay audience',
      ],
    },
    {
      blurb:
        'Consumer insight at a brand you know — reconcile what customers say with what they do, and hand the product team a decision, not a deck.',
      company: 'Beats by Dre',
      duration: '8 weeks',
      image: REC_IMAGES.cards[0],
      logo: '',
      tags: ['Market Research', 'Consumer Insights', 'Survey Design'],
      title: 'Consumer Behavior & Market Analysis',
      whatYoullLearn: [
        'Design a survey that produces usable data',
        'Run a competitor and consumer analysis',
        'Turn an insight into a recommendation',
        'Brief a brand team on what to do next',
      ],
    },
    {
      blurb:
        'Data that has to change a policy decision — visualize youth-justice data for the people who set the rules, where the read has real consequences.',
      company: 'Center for Improving Youth Justice',
      duration: '6 weeks',
      image: REC_IMAGES.cards[1],
      logo: '',
      tags: ['Visual Data Storytelling', 'Design', 'Interactive Visualizations'],
      title: 'Data Visualization',
      whatYoullLearn: [
        'Work with real public-sector data',
        'Visualize a finding for policymakers',
        'Handle a dataset with real stakes',
        'Make the case for a change and defend it',
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// The pack
// ---------------------------------------------------------------------------

export const DATA_ANALYTICS_PACK: VerticalPack = {
  archetypes: DATA_ARCHETYPES,
  axis: DATA_AXIS,
  // Must be verbatim one of the application's career-focus options, or the entry never shows to anyone.
  careerFocusArea: 'Data Analytics & Visualization',
  copy: DATA_ANALYTICS_COPY,
  deriveArchetype,
  displayName: 'Data & Analytics',
  events: DATA_DISCOVERY_EVENTS,
  featureFlag: 'externship_labs_data_q3_2026',
  m1: { cards: M1_CARDS },
  m2: { questions: M2_QUESTIONS },
  // Mirrors finance's "Finance Sprint" rail; LineChart is the engine's data-axis icon.
  nav: { icon: LineChart, label: 'Data Sprint', mobileLabel: 'Data' },
  m3: {
    metrics: M3_METRICS,
    subject: M3_SUBJECT,
    tolerance: METRIC_TOLERANCE,
    unitSuffix: METRIC_UNIT_SUFFIX,
  },
  m4: {
    answerKey: M4_ANSWER_KEY,
    groups: M4_GROUPS,
    sampleAnalyses: M4_SAMPLE_ANALYSES,
    subjects: M4_SUBJECTS,
    takeSamples: M4_TAKE_SAMPLES,
  },
  recommendations: {
    archetypePriority: ARCHETYPE_PRIORITY,
    categories: DATA_CATEGORIES,
    fallback: RECOMMENDATIONS,
  },
  slug: 'data-analytics',
};
